import React, { forwardRef, useCallback } from 'react';
import { useWtrDispatch } from 'redux/hooks';
import classNames from 'classnames';
import { Chip, ChipsGroup } from '@johnlewispartnership/wtr-ingredients/ingredients/Chips';
import { SSRMediaQuery } from 'components/SSRMediaQuery';
import { BREAKPOINTS } from 'constants/grid';
import { OFFER_PRODUCTS_LOADED } from 'redux/modules/search-and-browse/actions/types';
import { fetchComponentsAndProducts } from 'redux/modules/search-and-browse/actions/fetch-components-and-products';

import styles from './OfferDetails.scss';

type Category = { name: string; id: number };

const ALL_CATEGORY_ID = 0;

export const filterChips: Array<Category> = [
  { name: 'All', id: ALL_CATEGORY_ID },
  { name: 'Red Wine', id: 300494 },
  { name: 'White Wine', id: 300495 },
  { name: 'Rose Wine', id: 300496 },
  { name: 'Fine Wine', id: 300497 },
  { name: 'Champagne & Sparkling Wine', id: 300498 },
  { name: 'Dessert Wine', id: 300499 },
  { name: 'Port, Sherry & Madeira', id: 300629 },
];

type WineOfferFilterChipProps = {
  activeChipId: number;
  id: number;
  name: string;
  setActiveChipId: React.Dispatch<React.SetStateAction<number>>;
};

const WineOfferFilterChip = ({
  activeChipId,
  id,
  name,
  setActiveChipId,
}: WineOfferFilterChipProps) => {
  const isActive = activeChipId === id;
  const accessibleLabel = name === 'All' ? 'Show all products' : `Filter products by ${name}`;
  const dispatch = useWtrDispatch();

  const handleClick = useCallback(() => {
    if (isActive) {
      return;
    }

    dispatch(
      fetchComponentsAndProducts({
        categoryId: id,
        categoryLevel: id === ALL_CATEGORY_ID ? 0 : 3,
        successType: OFFER_PRODUCTS_LOADED,
      }),
    );

    setActiveChipId(id);
  }, [dispatch, id, isActive, setActiveChipId]);

  return (
    <Chip aria-label={accessibleLabel} onClick={handleClick} active={isActive}>
      <span className={styles.chipText}>{name}</span>
    </Chip>
  );
};

type WineOfferFilterChipsProps = {
  mobile?: boolean;
  sticky?: boolean;
  hidden?: boolean;
  activeChipId: number;
  setActiveChipId: React.Dispatch<React.SetStateAction<number>>;
};

const WineOfferFilterChips = forwardRef<HTMLDivElement, WineOfferFilterChipsProps>(
  (
    {
      mobile = false,
      sticky = false,
      hidden = false,
      activeChipId,
      setActiveChipId,
    }: WineOfferFilterChipsProps,
    ref,
  ) => {
    const mediaQueryProps = mobile ? { maxWidth: BREAKPOINTS.lg } : { minWidth: BREAKPOINTS.lg };

    return (
      <SSRMediaQuery {...mediaQueryProps}>
        <section
          className={classNames(styles.chipsWrapper, {
            [styles.mobile]: mobile,
            [styles.sticky]: sticky,
            [styles.hidden]: hidden,
          })}
          aria-label="Filter by category"
          data-testid="filter-chips-wrapper"
          ref={ref}
        >
          <ChipsGroup
            scrollable
            className="container-fluid"
            {...(!mobile && { backgroundColor: 'scallop' })}
          >
            {filterChips.map(({ name, id }: Category) => (
              <WineOfferFilterChip
                activeChipId={activeChipId}
                id={id}
                key={id}
                name={name}
                setActiveChipId={setActiveChipId}
              />
            ))}
          </ChipsGroup>
        </section>
      </SSRMediaQuery>
    );
  },
);

export default WineOfferFilterChips;
